import React, { useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";

import Guid from '_shared/guids/guid';
import { styled } from '@mui/material/styles';
import { useSession } from 'users/__session/session-context';
import { CanceledError } from 'axios';
import SystemNotification from 'notifications/system-notification/system-notification';
import SuperUserAPIService from 'users/super-users/_api/super-user-api-service';

const NotificationPaper = styled(Paper)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2)
  },
  padding: theme.spacing(6),
  position: 'relative'
}));
const NotificationForm = styled(Box)(() => ({
  maxWidth: '15rem'
}));
const NotificationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5)
}));
const NotificationInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: '20rem'
}));
const NotificationSelect = styled(Select)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: '20rem'
}));

export default function SystemNotifications() {
  const [notificationType, setNotificationType] = React.useState<string>('');
  const [notificationContent, setNotificationContent] = React.useState('');
  const [formValid, setFormValid] = React.useState(false);
  const [systemNotifications, setSystemNotifications] = React.useState<SystemNotification[]>([]);
  const [saving, setSaving] = React.useState(false);

  const session = useSession();

  useEffect(() => {
    let abortController = new AbortController();
    void getNotifications(abortController);
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };

  });

  async function createNotification() {
    setSaving(true);

    try {
      const superUserAPIService = new SuperUserAPIService(session);
      await superUserAPIService.createSystemNotification(notificationType, notificationContent);
      clearForm();
      await getNotifications();
    }
    catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to create system notification", { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function getNotifications(abortController?: AbortController) {
    try {
      const superUserAPIService = new SuperUserAPIService(session);
      let notifications = await superUserAPIService.getSystemNotifications(abortController);
      setSystemNotifications(notifications);
    }
    catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar("Unable to fetch system notifications...", { variant: 'error' });
    } 
  }

  async function deleteNotification(notificationId: Guid) {
    try {
      const superUserAPIService = new SuperUserAPIService(session);
      await superUserAPIService.deleteSystemNotification(notificationId);
      await getNotifications();
    }
    catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to delete system notification", { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function validateForm() {
    setFormValid(notificationType !== '' && notificationContent !== '');
  }

  function clearForm() {
    setNotificationType('');
    setNotificationContent('');
    setFormValid(false);
  }

  return (
    <React.Fragment>
      <NotificationPaper>
        <NotificationForm>
          <NotificationFormTitle variant='h6'>
            New System Notification
          </NotificationFormTitle>
          <InputLabel id="notification-type-select-label">Notification Type</InputLabel>
          <NotificationSelect
            labelId="notification-type-select-label"
            id="notification-type-select"
            value={notificationType}
            label="Select the notification type..."
            onChange={(event) => {
              setNotificationType(event.target.value as string);
              validateForm();
            }}>
            <MenuItem value={'info'}>Info</MenuItem>
            <MenuItem value={'warning'}>Warning</MenuItem>
            <MenuItem value={'alert'}>Alert</MenuItem>
          </NotificationSelect>
          <NotificationInput
            label="Notification"
            minRows={5}
            multiline
            value={notificationContent}
            variant="outlined"
            onChange={(event) => {
              setNotificationContent(event.target.value);
              validateForm();
            }}
          />
          <Button
            color="primary"
            variant="contained"
            disabled={saving || !formValid}
            onClick={createNotification}>
            Send Notification
          </Button>
        </NotificationForm>
      </NotificationPaper>
      <NotificationPaper>
        <NotificationForm>
          <NotificationFormTitle variant='h6'>
            Current System Notifications
          </NotificationFormTitle>
        </NotificationForm>
        {systemNotifications &&
          <Table aria-label="notification table">
            <TableHead>
              <TableRow>
                <TableCell align="left">Type</TableCell>
                <TableCell align="left">Notification</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {systemNotifications.map((row) => (
                <TableRow key={row.id.value}>
                  <TableCell align="left">{row.type}</TableCell>
                  <TableCell align="left">{row.content}</TableCell>
                  <TableCell align='right'>
                    <Button
                      onClick={() => deleteNotification(row.id)}
                      color="primary">
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        }
      </NotificationPaper>
    </React.Fragment>
  )
}
